import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  useMediaQuery,
  Menu,
  ListItemIcon,
} from '@mui/material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  addSupportLink,
  deleteSupportLink,
  updateSupportLink,
  getAllSupportLinks,
} from '@/repository/SupportLinksDAO';
import { useUserContext } from '@/context_providers/user_context/UserProvider';
import ConfirmModal from '@/components/modals/utils/ConfirmModal';
import Loading from '@/components/loading/Loading';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { ToastType } from '@/context_providers/toast_context/ToastContext';
import LoadingSpinner from '@/components/loading/LoadingSpinner';
import COLORS from '@/resources/style/Themes';
import EditSupportLinkModal from './modals/EditSupportLinkModal';
import CreateSupportLinkModal from './modals/CreateSupportLinkModal';
import { Add, Delete, Edit, MoreVert, Translate } from '@mui/icons-material';
import DefaultIconButton from '@/components/utils/DefaultIconButton';
import { getAllLanguageCodes } from '@/repository/LanguageCodesDAO';
import MissingTranslationsModal from './modals/MissingTranslationsModal';
import {
  getMissingTranslations as fetchMissingTranslations,
  SUPPORT_LINK_OBJECT_TYPE,
} from '@/repository/TranslationDAO';
import {
  SupportLinkCreate,
  SupportLinkOnAzure,
  SupportLinkUpdate,
  TranslationDraft,
} from '@/../generated-client/models';

export const SupportLinks = () => {
  const [responseData, setResponseData] = useState<SupportLinkOnAzure[]>([]);
  const [filteredData, setFilteredData] = useState<SupportLinkOnAzure[]>([]);
  const { bearerToken } = useUserContext();
  const [supportLinkToEdit, setSupportLinkToEdit] =
    useState<SupportLinkOnAzure>();
  const [supportLinkToHighlight, setSupportLinkToHighlight] = useState<
    string | null
  >(null);
  const [showEditSupportLinkModal, setShowEditSupportLinkModal] =
    useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [supportLinkToRemove, setSupportLinkToRemove] =
    useState<SupportLinkOnAzure>();
  const [showAddSupportLinkModal, setShowAddSupportLinkModal] = useState(false);
  const [filters, setFilters] = useState({
    link_type: '',
    language_code: '',
    section_type: '',
    is_active: '',
    name: '',
  });
  const { showToast } = useToast();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [languageCodes, setLanguageCodes] = useState<{
    [key: string]: string;
  }>({});
  const [openTranslationsModal, setOpenTranslationsModal] = useState(false);
  const [currentItem, setCurrentItem] = useState<SupportLinkOnAzure | null>(
    null,
  );
  const [translations, setTranslations] = useState<
    (
      | {
          languageName: string;
          languageCode: string;
          translatedText: any;
          original: string;
          newSupportLink: {
            header: any;
            is_active: boolean | undefined;
            language_code: string;
            link_type: string;
            name: string;
            section_type: string;
            url: string;
          };
          status: string;
        }
      | {
          languageName: string;
          languageCode: string;
          error: any;
          status: string;
        }
    )[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTranslatingLanguage, setCurrentTranslatingLanguage] =
    useState('');

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (supportLinkToHighlight != null) {
      setTimeout(() => {
        setSupportLinkToHighlight(null);
      }, 2000);
    }
  }, [supportLinkToHighlight]);

  useEffect(() => {
    const fetchSupportLinkData = async () => {
      try {
        const result = await getAllSupportLinks();
        setResponseData(result);
        setFilteredData(result);
      } catch (error) {
        console.error(error);
      }
    };
    fetchSupportLinkData();
  }, [bearerToken]);

  useEffect(() => {
    const fetchLanguageCodes = async () => {
      try {
        const result = await getAllLanguageCodes();

        setLanguageCodes(result);
      } catch (error) {
        showToast(
          `Failed to fetch language codes. Please try again.`,
          ToastType.DANGER,
        );
        console.error(error);
      }
    };
    fetchLanguageCodes();
  }, [bearerToken]);

  const updatedSupportLinkLocal = async (
    editedSupportLink: SupportLinkUpdate,
  ) => {
    try {
      await updateSupportLink(editedSupportLink);
      const updatedSupportLink: SupportLinkOnAzure = {
        header: editedSupportLink.header,
        is_active: editedSupportLink.is_active,
        link_type: editedSupportLink.link_type,
        name: editedSupportLink.name,
        section_type: editedSupportLink.section_type,
        url: editedSupportLink.url,
        language_code: editedSupportLink.language_code,
        id: editedSupportLink.id,
        object_type: 'support_link',
        _etag: '',
        _ts: 0,
      };

      const index = responseData.findIndex(
        (supportLink) => supportLink.id === updatedSupportLink.id,
      );
      const updatedData = [...responseData];
      updatedData[index] = updatedSupportLink;
      setResponseData(updatedData);
      showToast(
        `Successfully updated ${updatedSupportLink.header} support link.`,
        ToastType.SUCCESS,
      );
      setSupportLinkToHighlight(updatedSupportLink.id);
    } catch (e) {
      showToast(
        `Failed to update ${editedSupportLink.header}`,
        ToastType.DANGER,
      );
    }
  };

  const closeTranslationsModal = () => {
    setOpenTranslationsModal(false);
    setTranslations([]);
    setCurrentItem(null);
    setCurrentTranslatingLanguage('');
  };

  const editSupportLink = (supportLink: SupportLinkOnAzure) => {
    setSupportLinkToEdit(supportLink);
    setShowEditSupportLinkModal(true);
  };

  const deleteSupportLinkLocal = async () => {
    if (!supportLinkToRemove) {
      console.error('Support link you tried to remove does not exist');
      showToast('That Support Link does not exist. Try refreshing the page.');
      return;
    }

    try {
      setShowConfirmModal(false);
      await deleteSupportLink(supportLinkToRemove);
      const newResponseData = responseData.filter(
        (supportLink) => supportLink.id !== supportLinkToRemove.id,
      );
      setResponseData(newResponseData);
      showToast(
        `Successfully removed support link ${supportLinkToRemove.header}.`,
        ToastType.SUCCESS,
      );
    } catch (e) {
      showToast(
        `Failed to remove support link ${supportLinkToRemove.header}. Please try again.`,
        ToastType.DANGER,
      );
    } finally {
      setSupportLinkToRemove(undefined);
    }
  };

  const addSupportLinkLocal = async (newSupportLink: SupportLinkCreate) => {
    try {
      const createdSupportLink = await addSupportLink(newSupportLink);

      showToast(
        `Successfully added ${newSupportLink.header} configuration.`,
        ToastType.SUCCESS,
      );
      setSupportLinkToHighlight(createdSupportLink.id);
      setResponseData((prevData) => [...prevData, createdSupportLink]);
    } catch (error) {
      console.error(error);
      showToast(
        `Failed to add ${newSupportLink.header} support link. Please try again.`,
        ToastType.DANGER,
      );
    }
  };

  useEffect(() => {
    const applyFilters = () => {
      let data = [...responseData];
      if (filters.link_type) {
        data = data.filter((item) => item.link_type === filters.link_type);
      }
      if (filters.language_code) {
        data = data.filter(
          (item) => item.language_code === filters.language_code,
        );
      }
      if (filters.section_type) {
        data = data.filter(
          (item) => item.section_type === filters.section_type,
        );
      }
      if (filters.is_active) {
        data = data.filter(
          (item) => item?.is_active?.toString() === filters.is_active,
        );
      }
      if (filters.name) {
        data = data.filter((item) => item.name === filters.name);
      }
      setFilteredData(data);
    };
    applyFilters();
  }, [filters, responseData]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
  };

  const renderFilters = () => (
    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
      <FormControl sx={{ minWidth: 150 }}>
        <InputLabel>Link Type</InputLabel>
        <Select
          value={filters.link_type}
          onChange={(e) => handleFilterChange('link_type', e.target.value)}
          label="Link Type"
        >
          <MenuItem value="">All</MenuItem>
          {/* Add specific link types dynamically */}
          {[...new Set(responseData.map((item) => item.link_type))].map(
            (type) => (
              <MenuItem value={type} key={type}>
                {type}
              </MenuItem>
            ),
          )}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 150 }}>
        <InputLabel>Name</InputLabel>
        <Select
          value={filters.name}
          onChange={(e) => handleFilterChange('name', e.target.value)}
          label="Name"
        >
          <MenuItem value="">All</MenuItem>
          {[...new Set(responseData.map((item) => item.name))].map((name) => (
            <MenuItem value={name} key={name}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 150 }}>
        <InputLabel>Language Code</InputLabel>
        <Select
          value={filters.language_code}
          onChange={(e) => handleFilterChange('language_code', e.target.value)}
          label="Language Code"
        >
          <MenuItem value="">All</MenuItem>
          {[...new Set(responseData.map((item) => item.language_code))].map(
            (code) => (
              <MenuItem value={code} key={code}>
                {code}
              </MenuItem>
            ),
          )}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 150 }}>
        <InputLabel>Section Type</InputLabel>
        <Select
          value={filters.section_type}
          onChange={(e) => handleFilterChange('section_type', e.target.value)}
          label="Section Type"
        >
          <MenuItem value="">All</MenuItem>
          {[...new Set(responseData.map((item) => item.section_type))].map(
            (section) => (
              <MenuItem value={section} key={section}>
                {section}
              </MenuItem>
            ),
          )}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 150 }}>
        <InputLabel>Active</InputLabel>
        <Select
          value={filters.is_active}
          onChange={(e) => handleFilterChange('is_active', e.target.value)}
          label="Active"
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  const checkMissingTranslations = (name: string, languageCode: string) => {
    if (
      !Object.keys(languageCodes).length ||
      languageCode.toLowerCase() !== 'eng'
    )
      return false;

    const translations = responseData.filter((item) => item.name === name);
    const existingLanguages = translations.map((t) =>
      t.language_code.toLowerCase(),
    );
    const codes = Object.values(languageCodes);

    return codes.some(
      (code) =>
        code.toLowerCase() !== 'eng' &&
        !existingLanguages.includes(code.toLowerCase()),
    );
  };

  const getMissingTranslations = async (item: SupportLinkOnAzure) => {
    setIsLoading(true);
    try {
      const response = await fetchMissingTranslations({
        object_type: SUPPORT_LINK_OBJECT_TYPE,
        id: item.id,
      });
      const drafts = response.drafts ?? [];
      if (drafts.length === 0) {
        closeTranslationsModal();
        return;
      }

      setTranslations(
        drafts.map((draft: TranslationDraft) => {
          const isError = (draft.status ?? '').toLowerCase() === 'error';
          if (isError) {
            return {
              languageName: draft.language_name ?? '',
              languageCode: draft.language_code ?? '',
              error: draft.error || 'Failed to translate',
              status: 'error',
            };
          }

          const translatedText = draft.header ?? '';
          return {
            languageName: draft.language_name ?? '',
            languageCode: draft.language_code ?? '',
            translatedText,
            original: item.header,
            newSupportLink: {
              header: translatedText,
              is_active: draft.is_active ?? item.is_active,
              language_code: draft.language_code ?? '',
              link_type: draft.link_type ?? item.link_type,
              name: draft.name ?? item.name,
              section_type: draft.section_type ?? item.section_type,
              url: draft.url ?? item.url,
            },
            status: 'success',
          };
        }),
      );
    } catch (error) {
      closeTranslationsModal();
      showToast(
        'Failed to fetch translations. Please try again.',
        ToastType.DANGER,
      );
    } finally {
      setIsLoading(false);
      setCurrentTranslatingLanguage('');
    }
  };

  const renderData = () => (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
          mb: 2,
          width: '100%',
          flexWrap: 'wrap', // Allows wrapping on smaller screens
        }}
      >
        {/* Title */}
        <Typography variant="h5" gutterBottom sx={{ flexShrink: 0 }}>
          Support Links
        </Typography>

        {/* Filters */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexGrow: 1,
            mx: 2,
          }}
        >
          {renderFilters()}
        </Box>

        {/* Add Button */}
        {isMobile ? (
          <>
            <IconButton onClick={handleMenuClick}>
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  setShowAddSupportLinkModal(true);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <Add />
                </ListItemIcon>
                Add New
              </MenuItem>
            </Menu>
          </>
        ) : (
          <DefaultIconButton
            disabled={false}
            action={() => setShowAddSupportLinkModal(true)}
            icon={<Add />}
            label="Add New"
            sx={{ flexShrink: 0 }}
          />
        )}
      </Box>
      <Typography variant="h5" color="error" align="center" gutterBottom>
        IMPORTANT! DO NOT EDIT/ADD/DELETE WITHOUT DISCUSSION WITH SOFTWARE TEAM.
        LINKS IMPACT MOBILE APPLICATIONS.
      </Typography>
      <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Header</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Language Code</TableCell>
              <TableCell>Link Type</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Section Type</TableCell>
              <TableCell>URL</TableCell>
              <TableCell align="center">Translations</TableCell>
              <TableCell align="center">Edit</TableCell>
              <TableCell align="center">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((data, index) => (
              <TableRow
                key={index}
                className={
                  data.id === supportLinkToHighlight ? 'flash-green' : ''
                }
              >
                <TableCell>{data.header}</TableCell>
                <TableCell>{data.is_active ? 'Yes' : 'No'}</TableCell>
                <TableCell>{data.language_code}</TableCell>
                <TableCell>{data.link_type}</TableCell>
                <TableCell>{data.name}</TableCell>
                <TableCell>{data.section_type}</TableCell>
                <TableCell>{data.url}</TableCell>
                <TableCell align="center">
                  {Object.keys(languageCodes).length > 0 &&
                    checkMissingTranslations(data.name, data.language_code) && (
                      <Tooltip title="Missing Translations">
                        <IconButton
                          aria-label="Missing Translations"
                          onClick={() => {
                            setCurrentItem(data);
                            setOpenTranslationsModal(true);
                            getMissingTranslations(data);
                          }}
                          sx={{
                            borderRadius: '10px',
                            width: '40px',
                            height: '40px',
                            backgroundColor: COLORS.LIGHT_BLUE,
                            '&:hover': {
                              backgroundColor: COLORS.HOVER_BLUE,
                            },
                          }}
                        >
                          <Translate sx={{ color: COLORS.DARK_BLUE }} />
                        </IconButton>
                      </Tooltip>
                    )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={() => editSupportLink(data)}
                      sx={{
                        borderRadius: '10px',
                        width: '40px',
                        height: '40px',
                        backgroundColor: COLORS.LIGHT_GREEN,
                        '&:hover': {
                          backgroundColor: COLORS.HOVER_GREEN,
                        },
                      }}
                    >
                      <Edit sx={{ color: COLORS.DARK_GREEN }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => {
                        setConfirmTitle(`Delete ${data.header}`);
                        setConfirmMessage(
                          `Are you sure you would like to delete message ${data.header}?`,
                        );
                        setSupportLinkToRemove(data);
                        setShowConfirmModal(true);
                      }}
                      sx={{
                        borderRadius: '10px',
                        width: '40px',
                        height: '40px',
                        backgroundColor: COLORS.PINK,
                        '&:hover': {
                          backgroundColor: COLORS.HOVER_RED,
                        },
                      }}
                    >
                      <Delete sx={{ color: COLORS.DARK_RED }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  return (
    <>
      <Container maxWidth="xl" sx={{ paddingBottom: '50px' }}>
        {responseData ? renderData() : <LoadingSpinner />}

        <EditSupportLinkModal
          show={showEditSupportLinkModal}
          onHide={() => setShowEditSupportLinkModal(false)}
          supportLink={supportLinkToEdit}
          onSave={updatedSupportLinkLocal}
        />

        <CreateSupportLinkModal
          show={showAddSupportLinkModal}
          onHide={() => setShowAddSupportLinkModal(false)}
          onCreate={addSupportLinkLocal}
        />

        <ConfirmModal
          show={showConfirmModal}
          confirm={deleteSupportLinkLocal}
          onHide={() => {
            setSupportLinkToRemove(undefined);
            setShowConfirmModal(false);
          }}
          confirmMessage={confirmMessage}
          confirmTitle={confirmTitle}
        />

        <MissingTranslationsModal
          open={openTranslationsModal}
          onClose={closeTranslationsModal}
          currentItem={currentItem}
          translations={translations}
          isLoading={isLoading}
          currentTranslatingLanguage={currentTranslatingLanguage}
          onAdd={async (newSupportLink: SupportLinkCreate) => {
            try {
              const addedLink = await addSupportLink(newSupportLink);

              setResponseData((prevData) => [...prevData, addedLink]);
              showToast(
                `Successfully added ${newSupportLink.header} support link.`,
                ToastType.SUCCESS,
              );
              return true;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : '';
              showToast(
                `Failed to add ${newSupportLink.header} support link: ${errorMessage}`,
                ToastType.DANGER,
              );
              return false;
            }
          }}
        />
      </Container>
    </>
  );
};

export default withAuthenticationRequired(SupportLinks, {
  onRedirecting: () => <Loading loadingText="Loading..." />,
});
